import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from '../services/api';
import './AdminAnnouncements.css';

const EMPTY_FORM = { title: '', content: '', is_active: true };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const formRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = () =>
    getAnnouncements().then(setItems).catch(() => setError('Impossible de charger les annonces.'));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.title.trim() || !form.content.trim()) {
      setError('Le titre et le contenu sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateAnnouncement(editingId, form);
        setSuccess('Annonce mise à jour.');
      } else {
        await createAnnouncement(form);
        setSuccess('Annonce créée.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, content: item.content, is_active: item.is_active });
    setEditingId(item.id);
    setError(''); setSuccess('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await deleteAnnouncement(id);
      setSuccess('Annonce supprimée.');
      load();
    } catch {
      setError('Impossible de supprimer.');
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError(''); setSuccess('');
  };

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="aa-page">
      <div className="aa-header">
        <div className="container">
          <Link to="/gestion-ecclesiale" className="aa-breadcrumb">← Tableau de bord</Link>
          <h1 className="aa-title">Annonces 📢</h1>
        </div>
      </div>

      <div className="container aa-body">
        {/* ── Form ── */}
        <div className="aa-form-section" ref={formRef}>
          <h2 className="aa-section-title">
            {editingId ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
          </h2>
          {error && <div className="aa-alert aa-alert--error">{error}</div>}
          {success && <div className="aa-alert aa-alert--success">{success}</div>}
          <form className="aa-form" onSubmit={handleSubmit}>
            <div className="aa-field">
              <label htmlFor="aa-title">Titre *</label>
              <input
                id="aa-title"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Ex: Collecte de fonds pour les sinistrés"
                maxLength={200}
              />
            </div>
            <div className="aa-field">
              <label htmlFor="aa-content">Contenu *</label>
              <textarea
                id="aa-content"
                name="content"
                value={form.content}
                onChange={handleChange}
                placeholder="Détails de l'annonce..."
                rows={4}
              />
            </div>
            <label className="aa-toggle">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />
              <span className="aa-toggle__track" />
              <span className="aa-toggle__label">Annonce visible sur le site</span>
            </label>
            <div className="aa-form-actions">
              <button type="submit" className="aa-btn aa-btn--primary" disabled={saving}>
                {saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Publier l\'annonce'}
              </button>
              {editingId && (
                <button type="button" className="aa-btn aa-btn--ghost" onClick={handleCancel}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── List ── */}
        <div className="aa-list-section">
          <h2 className="aa-section-title">Toutes les annonces ({items.length})</h2>
          {items.length === 0 ? (
            <p className="aa-empty">Aucune annonce pour le moment.</p>
          ) : (
            <div className="aa-list">
              {items.map((item) => (
                <div key={item.id} className={`aa-card${editingId === item.id ? ' aa-card--editing' : ''}`}>
                  <div className="aa-card__head">
                    <div>
                      <h3 className="aa-card__title">{item.title}</h3>
                      <span className="aa-card__date">{formatDate(item.date)}</span>
                    </div>
                    <span className={`aa-badge ${item.is_active ? 'aa-badge--active' : 'aa-badge--inactive'}`}>
                      {item.is_active ? 'Visible' : 'Masquée'}
                    </span>
                  </div>
                  <p className="aa-card__content">{item.content}</p>
                  <div className="aa-card__actions">
                    <button className="aa-action-btn aa-action-btn--edit" onClick={() => handleEdit(item)}>
                      ✏️ Modifier
                    </button>
                    <button className="aa-action-btn aa-action-btn--delete" onClick={() => handleDelete(item.id)}>
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
